import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { X, Send, Loader2, MessageCircle, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// El primer WhatsApp a un propietario.
//
// Se redacta con los datos reales de SU anuncio y se enseña ANTES de mandarlo.
// Nunca se envía a ciegas: esto entra en el móvil personal de alguien que no ha
// pedido nada, y quien firma el mensaje eres tú, no la máquina.

export function MensajeWhatsapp({ anuncio, orgId, onCerrar, onEnviado }) {
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [texto, setTexto] = useState('');
  const [telefono, setTelefono] = useState('');
  const [error, setError] = useState('');

  const redactar = async () => {
    setCargando(true); setError('');
    try {
      const r = await axios.post(
        `${API}/crm/portales/listings/${anuncio.listing_id}/redactar`, { org_id: orgId });
      setTexto(r.data?.texto || '');
      setTelefono(r.data?.telefono || '');
    } catch (err) {
      setError(err.response?.data?.detail || 'No se pudo redactar el mensaje');
    }
    setCargando(false);
  };

  useEffect(() => { redactar(); /* eslint-disable-next-line */ }, [anuncio.listing_id]);

  const enviar = async () => {
    if (!texto.trim()) return;
    setEnviando(true);
    try {
      await axios.post(`${API}/crm/portales/listings/${anuncio.listing_id}/whatsapp`,
        { org_id: orgId, texto, telefono });
      toast.success('Mensaje enviado. La respuesta entra en la sección WhatsApp.');
      onEnviado();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo enviar');
    }
    setEnviando(false);
  };

  const palabras = texto.trim() ? texto.trim().split(/\s+/).length : 0;

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-start md:items-center justify-center p-3 overflow-y-auto"
      onClick={onCerrar}>
      <motion.div initial={{ scale: 0.97, y: 10 }} animate={{ scale: 1, y: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-[#0f172a] border border-white/10 rounded-2xl w-full max-w-xl my-6 shadow-2xl">

        <div className="flex items-start gap-3 p-5 border-b border-white/8">
          <MessageCircle size={20} className="text-emerald-400 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <h3 className="text-white font-bold text-lg">Primer WhatsApp</h3>
            <p className="text-white/40 text-xs mt-0.5 truncate">
              {anuncio.titulo}
              {anuncio.origen && <span className="ml-2 px-1.5 py-0.5 rounded bg-white/8 text-[10px]">{anuncio.origen}</span>}
              {anuncio.dias_en_mercado ? <span className="ml-2">· {anuncio.dias_en_mercado} días publicado</span> : null}
            </p>
          </div>
          <button onClick={onCerrar} className="text-white/40 hover:text-white p-1"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-3">
          {cargando ? (
            <div className="flex items-center gap-2 text-white/50 text-sm py-8 justify-center">
              <Loader2 size={16} className="animate-spin" /> Redactándolo con los datos de su anuncio…
            </div>
          ) : error ? (
            <div className="rounded-lg border p-3 text-sm"
                 style={{ background: 'rgba(239,68,68,0.08)', borderColor: 'rgba(239,68,68,0.3)' }}>
              <p className="text-red-300">{error}</p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <label className="text-white/40 text-[11px] uppercase tracking-wide">
                  Va a {telefono}
                </label>
                <button onClick={redactar}
                  className="text-white/40 hover:text-white text-[11px] flex items-center gap-1">
                  <RefreshCw size={11} /> otra versión
                </button>
              </div>
              <textarea value={texto} onChange={(e) => setTexto(e.target.value)} rows={7}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/85 focus:outline-none focus:border-emerald-500/50 resize-none" />
              <p className="text-white/30 text-[11px]">
                {palabras} palabras · léelo antes de mandarlo, y cámbialo si no suena a ti.
                Entra en su móvil personal.
              </p>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 p-5 border-t border-white/8">
          <button onClick={onCerrar}
            className="px-4 py-2 rounded-lg text-sm text-white/60 hover:text-white border border-white/10">
            Cancelar
          </button>
          <button onClick={enviar} disabled={enviando || cargando || !texto.trim()}
            className="px-5 py-2 rounded-lg text-sm font-bold bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-30 flex items-center gap-2">
            {enviando ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Enviar por WhatsApp
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default MensajeWhatsapp;
